"use client";;
import { font_text_varela } from "@/app/ui/fonts";
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuLabel, ContextMenuSeparator, ContextMenuTrigger } from "@/app/ui/imported/context-menu";
import CopyContent from "@/app/ui/utils/CopyContent";
import Image from "@/app/ui/utils/Image";
import {
    cn,
    GoogleAnalyticsEvent,
    youtube_getVideoLink,
    youtube_getVideoThumbnail,
    youtube_isVideoRecent,
} from "@/lib/utils";
import Link from "next/link";
import Script from "next/script";
import { MouseEvent, ReactNode, useContext, useRef } from "react";
import { AiFillYoutube } from "react-icons/ai";
import { FaUserAlt } from "react-icons/fa";
import { IoIosCopy } from "react-icons/io";
import { VideoObject, WithContext } from "schema-dts";
import { ShortsNavigatorContext } from "./ShortsNavigator_client";

export default function ShortVideoItem({ video }: { video: { id: string, title: string, ytid: string, createdAt: Date } }) {
    const firstClick = useRef(true)
    const ctx = useContext(ShortsNavigatorContext);
    function handleClick(e: MouseEvent<HTMLDivElement | HTMLAnchorElement>) {
        //track only leftClick(1) & wheel Click(2)
        if (e.button >= 2)
            return;

        ctx.setCurrentVideo(video.id)
        ctx.setIsOpen(true)

        //prevent multiple GA events
        if (!firstClick.current)
            return;
        firstClick.current = false;
        GoogleAnalyticsEvent({
            event_name: "shortView",
            event_label: video.id,
        });
    };

    const ytLink = youtube_getVideoLink(video.id);
    const isRecentVideo = youtube_isVideoRecent(video);

    const jsonLd: WithContext<VideoObject> = {
        '@context': 'https://schema.org',
        '@type': 'VideoObject',
        name: video.title,
        description: `צפו עכשיו בפרק - ${video.title}`,
        thumbnailUrl: youtube_getVideoThumbnail(video.id),
        contentUrl: youtube_getVideoLink(video.id),
        uploadDate: new Date(video.createdAt).toISOString()
    }

    return (
        <VideoContext { ...{ ytLink, handleClick, video } }>
            <div
                className="flex flex-col justify-stretch h-full w-40 sm:w-72 group pb-4 cursor-pointer snap-center"
                onClick={ handleClick } // Logs the event
                onAuxClick={ handleClick }
            >
                <Title>{ video.title }</Title>

                <div className={ cn(" relative rounded-lg overflow-hidden aspect-[9/16] bg-gray-900 shadow-lg shadow-black/50",
                    isRecentVideo && " outline-yellow-400 outline outline-4"
                ) }>
                    <Image
                        fill
                        sizes="30vw"
                        src={ youtube_getVideoThumbnail(video.id) }
                        alt={ video.title }
                        className="w-full h-full object-cover rounded-[inherit] group-hover:scale-105 transition-transform duration-500 "
                    />
                </div>
            </div>
            <Script
                id="ShortVideoScheme"
                type="application/ld+json"
                dangerouslySetInnerHTML={ { __html: JSON.stringify(jsonLd) } }
            />
        </VideoContext>
    );
}

function Title({ children, className }: { children: ReactNode, className?: string }) {
    return <h3 role="heading" className={ cn("text-white mb-2 font-bold tracking-wide px-1 text-sm sm:text-md bg-black/30 rounded-md line-clamp-2 sm:line-clamp-1 text-shadow-sm text-shadow-black sm:leading-[2em_!important]",
        font_text_varela.className, className
    ) }>
        { children }
    </h3>
}


function VideoContext({ children, ytLink, handleClick, video }: { children: ReactNode, ytLink: string, video: { id: string, title: string, ytid: string }, handleClick: (e: MouseEvent<HTMLAnchorElement>) => void }) {
    return <ContextMenu>
        <ContextMenuTrigger>
            <>{ children }</>
        </ContextMenuTrigger>
        <ContextMenuContent>
            <ContextMenuLabel asChild className="py-0 px-0 text-2xl text-center"><h4>פעולות</h4></ContextMenuLabel>
            <ContextMenuSeparator />
            <ContextMenuItem asChild>
                <Link href={ ytLink } target="_blank" className="cursor-pointer" onClick={ handleClick }>
                    <AiFillYoutube className="me-2" size={ "1.5em" } /> פתח ביוטיוב
                </Link>
            </ContextMenuItem>
            <ContextMenuItem asChild>
                <Link href={ `/creator/${video.ytid}` } className="cursor-pointer">
                    <FaUserAlt className="me-2" size={ "1.5em" } /> עבור ליוצר
                </Link>
            </ContextMenuItem>
            <ContextMenuSeparator />
            <CopyContent as={ ContextMenuItem } asChild copy={ video.title }>
                <div className="cursor-copy">
                    <IoIosCopy className="me-2" size={ "1.5em" } /> העתק כותרת
                </div>
            </CopyContent>
        </ContextMenuContent>
    </ContextMenu>
}
